/**
 * SAY MY NAME — ORIGIN: the editorial story page.
 *
 * A brand/story page, not part of the naming conversation: no agent panel, no
 * input, no options. It is chrome-level rather than an authored surface (see
 * origin-page.ts for why), so it renders through a portal, layered over the stage
 * content at `z-[4]` — under the platform's sticky header wrapper (`z-[5]`), which
 * keeps the black header and its ORIGIN marker on top where they belong.
 *
 * The composition is the whole design: two columns divided by one hairline, four
 * typographic ranks, two horizontal rules, and a lot of deliberate negative
 * space. Nothing decorative — no photography, no cards, no icons, no giant
 * background words. The negative space is solved by alignment and hierarchy.
 *
 * It carries the one approved surface gradient (`.canvas-gradient`) over the
 * landing green, with BLACK type — the poster register, exactly like the landing
 * field. Black on this gradient measures 11.4:1 at the light end and 5.7:1 at the
 * deep end, so every rank here clears AA comfortably.
 *
 * Every word is fixed brand copy owned by this component. The model cannot write
 * or rewrite any of it.
 */
import { useEffect, type FC } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  headlineLine1: {
    id: 'origin.headline.line1',
    defaultMessage: 'WHY I',
    description: 'First line of the Origin page headline',
  },
  headlineLine2: {
    id: 'origin.headline.line2',
    defaultMessage: 'BUILT THIS.',
    description: 'Second line of the Origin page headline',
  },
  struggleLine1: {
    id: 'origin.struggle.line1',
    defaultMessage: 'I’VE STRUGGLED WITH NAMING',
    description: 'First line of the small caps standfirst under the Origin headline',
  },
  struggleLine2: {
    id: 'origin.struggle.line2',
    defaultMessage: 'MY OWN BUSINESSES FOR YEARS.',
    description: 'Second line of the small caps standfirst under the Origin headline',
  },
  aiLine1: {
    id: 'origin.ai.line1',
    defaultMessage: 'AI made generating names easier.',
    description: 'First line of the left column body copy',
  },
  aiLine2: {
    id: 'origin.ai.line2',
    defaultMessage: 'But I kept ending up with',
    description: 'Second line of the left column body copy',
  },
  aiLine3: {
    id: 'origin.ai.line3',
    defaultMessage: 'more names, not better strategy.',
    description: 'Emphasised closing line of the left column body copy',
  },
  process: {
    id: 'origin.process',
    defaultMessage:
      'When the obvious ideas weren’t working, I needed a process that knew when to change direction and look somewhere less obvious.',
    description: 'Opening paragraph of the right column',
  },
  lookingAt: {
    id: 'origin.lookingAt.label',
    defaultMessage: 'THAT MEANT LOOKING AT:',
    description: 'Small caps label introducing the territories the process explores',
  },
  territoriesLine1: {
    id: 'origin.territories.line1',
    defaultMessage: 'CULTURE / SLANG / HISTORY',
    description: 'First line of naming territories, slash separated. Not interactive.',
  },
  territoriesLine2: {
    id: 'origin.territories.line2',
    defaultMessage: 'PERSONAL EXPERIENCES / FORGOTTEN LANGUAGE',
    description: 'Second line of naming territories, slash separated. Not interactive.',
  },
  research: {
    id: 'origin.research',
    defaultMessage:
      'And once I found a name I loved, I wanted the research done before I committed to it.',
    description: 'Second section opening line of the right column',
  },
  researchDetail: {
    id: 'origin.research.detail',
    defaultMessage:
      'Companies have spent thousands, sometimes millions, dealing with trademark disputes and rebrands that started with the wrong name.',
    description: 'Supporting paragraph about the cost of the wrong name',
  },
  builtPrefix: {
    id: 'origin.built.prefix',
    defaultMessage: 'So I built',
    description: 'Words before the product name in the closing statement',
  },
  builtSuffix: {
    id: 'origin.built.suffix',
    defaultMessage: '.',
    description: 'Punctuation closing the final statement, after the product name',
  },
  promise: {
    id: 'origin.promise',
    defaultMessage: 'BETTER STRATEGY. DEEPER EXPLORATION. REAL RESEARCH.',
    description: 'Closing small caps line of the Origin page',
  },
  pageLabel: {
    id: 'origin.pageLabel',
    defaultMessage: 'Why I built Say My Name',
    description: 'Accessible name for the Origin story page region',
  },
});

/**
 * The product name as it is SET in this closing line: literal capitals, not a CSS
 * transform and not `toUpperCase()`. Case mapping is locale-dependent (Turkish
 * dotless i and friends), and a brand must read identically in every locale — so
 * the casing lives here as characters, the same rule the wordmark follows in
 * site-config.ts. `translate="no"` keeps machine translation off it.
 */
const PRODUCT_NAME_IN_CAPS = 'SAY MY NAME';

/** Small caps micro-type: the functional editorial register used across the studio. */
const CAPS = 'font-medium uppercase leading-relaxed tracking-caps';

export const OriginPage: FC = () => {
  const intl = useIntl();

  // The stage scrolls the page behind this overlay; lock that while the story is
  // open so one wheel gesture cannot move two surfaces at once.
  useEffect(() => {
    const { style } = document.body;
    const previous = style.overflow;
    style.overflow = 'hidden';
    return () => {
      style.overflow = previous;
    };
  }, []);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      className="canvas-gradient fixed inset-0 z-[4] flex overflow-y-auto bg-field text-field-ink"
      data-origin-page="open"
    >
      <main
        aria-label={intl.formatMessage(messages.pageLabel)}
        className="mx-auto my-auto flex w-full max-w-[1560px] flex-col gap-12 px-7 pb-20 pt-24 sm:px-12 sm:pb-24 sm:pt-28 lg:flex-row lg:gap-0 lg:px-20 lg:pb-24 lg:pt-24"
      >
        {/* LEFT — the headline, the admission, the problem. */}
        <div className="flex min-w-0 flex-col lg:basis-[52%] lg:pr-14 xl:pr-20">
          <h1 className="text-[2.5rem] font-bold leading-[0.88] tracking-[-0.02em] sm:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl">
            <span className="block">{intl.formatMessage(messages.headlineLine1)}</span>
            <span className="block">{intl.formatMessage(messages.headlineLine2)}</span>
          </h1>

          {/* The short rule: the reference's one piece of punctuation between the
              headline and the standfirst. */}
          <hr className="mt-10 w-28 border-0 border-t border-field-ink/30 sm:mt-12" />

          <p className={`mt-7 text-[11px] sm:text-[13px] ${CAPS}`}>
            <span className="block">{intl.formatMessage(messages.struggleLine1)}</span>
            <span className="block">{intl.formatMessage(messages.struggleLine2)}</span>
          </p>

          <p className="mt-9 text-lg leading-snug sm:mt-11 sm:text-xl lg:text-2xl">
            <span className="block">{intl.formatMessage(messages.aiLine1)}</span>
            <span className="block">{intl.formatMessage(messages.aiLine2)}</span>
            {/* The turn of the argument, and the reason the product exists. */}
            <span className="mt-1 block font-bold tracking-[-0.01em]">
              {intl.formatMessage(messages.aiLine3)}
            </span>
          </p>
        </div>

        {/* RIGHT — what that demanded, what it cost, what it became. The hairline
            is the column divider on wide viewports; stacked below, the sections
            carry their own rules and this one disappears. */}
        <div className="flex min-w-0 flex-col gap-10 lg:basis-[48%] lg:border-l lg:border-field-ink/25 lg:pl-14 xl:pl-20">
          <section className="flex flex-col gap-7">
            <p className="max-w-[40ch] text-base leading-snug sm:text-lg lg:text-xl">
              {intl.formatMessage(messages.process)}
            </p>

            <div className="flex flex-col gap-2.5">
              <p className={`text-[10px] text-field-ink/70 sm:text-[11px] ${CAPS} font-semibold`}>
                {intl.formatMessage(messages.lookingAt)}
              </p>
              {/* Territories, not categories: editorial type, nothing to click. */}
              <p className={`text-[11px] sm:text-[13px] ${CAPS}`}>
                <span className="block">{intl.formatMessage(messages.territoriesLine1)}</span>
                <span className="block">{intl.formatMessage(messages.territoriesLine2)}</span>
              </p>
            </div>
          </section>

          <section className="flex flex-col gap-5 border-t border-field-ink/25 pt-10">
            <p className="max-w-[40ch] text-base leading-snug sm:text-lg lg:text-xl">
              {intl.formatMessage(messages.research)}
            </p>
            <p className="max-w-[46ch] text-sm leading-relaxed text-field-ink/75 sm:text-base">
              {intl.formatMessage(messages.researchDetail)}
            </p>
          </section>

          <section className="flex flex-col gap-4 border-t border-field-ink/25 pt-10">
            <p className="text-2xl font-bold leading-tight tracking-[-0.02em] sm:text-3xl lg:text-4xl">
              {intl.formatMessage(messages.builtPrefix)}{' '}
              <span translate="no">{PRODUCT_NAME_IN_CAPS}</span>
              {intl.formatMessage(messages.builtSuffix)}
            </p>
            <p className={`text-[10px] sm:text-[11px] ${CAPS}`}>
              {intl.formatMessage(messages.promise)}
            </p>
          </section>
        </div>
      </main>
    </div>,
    document.body,
  );
};
